import {
  CheckBoxWithLabelWidget,
  getMaxCharValidation,
  TabStyles
} from '@tableau/widgets';
import React from 'react';
import shallowEqual = require('shallowequal');
import _ = require('underscore');

import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { divWithStyle } from '~src/style';

import { Messages } from '../../codegen/Localize';
import { LabelContentItem } from '../restApi/labels/LabelTypes';
import { makeFetchInitialStateForLabels } from '../restApi/labels/useLabels';
import { markupEditorStyle } from '../styles/DataQualityStyles';
import { TextFormatterOrAreaWidget } from './common/TextFormatterOrAreaWidget';
import { canSubmitLabel } from './dataQuality/canSubmitLabel';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

const CERTIFICATION_DIALOG_TEST_ID: string = 'certification-dialog';
const CERTIFICATION_DIALOG_TOGGLE_TEST_ID: string = CERTIFICATION_DIALOG_TEST_ID + '-toggle';

const textAreaStyle: React.CSSProperties = {
  resize: 'none',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
  height: 190,
  fontSize: TabStyles.Typography.Sizing.Default,
  marginBottom: TabStyles.Spacing.MediumSpace,
  padding: TabStyles.Spacing.DefaultSpace
};

const HelpTextDiv = divWithStyle(CERTIFICATION_DIALOG_TEST_ID, '-help-text', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

const checkboxStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.SmallSpace,
};

export type CertificationDialogState = {
  readonly certificationNote: string;
  readonly isCertified: boolean;
}

export type CertificationDialogBodyProps = {
  readonly contentItem: LabelContentItem;
  readonly loading?: boolean;
}
export type CertificationActionDialogOptions = ReactActionDialogOptions<CertificationDialogBodyProps, CertificationDialogState, unknown>;
export type CertificationDialogBodyComponentProps = ReactActionDialogBodyProps<CertificationDialogBodyProps, CertificationDialogState>;

function isFormDataValidForSubmission(args: {
  initialFormData: CertificationDialogState;
  currentFormData: CertificationDialogState;
  additionalValidation: (current: CertificationDialogState) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

export function CertificationDialogBody(props: CertificationDialogBodyComponentProps): React.ReactElement {
  const handleCertificationChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ certificationNote: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );
  const { message, valid } = getMaxCharValidation(props.dialogState.certificationNote, MESSAGE_MAXIMUM_LENGTH);

  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.certificationCheckboxLabel()}
        testId={CERTIFICATION_DIALOG_TOGGLE_TEST_ID}
        checked={props.dialogState.isCertified}
        handleChange={(checked: boolean) => props.updateDialogState({ isCertified: checked })}
        removeLeftMargin={true}
        containerStyle={checkboxStyle} />
      <HelpTextDiv>{Messages.certificationHelpNote()}</HelpTextDiv>
      <TextFormatterOrAreaWidget
        label={Messages.certificationNoteLabel()}
        onChange={handleCertificationChange}
        id={CERTIFICATION_DIALOG_TEST_ID}
        testId={CERTIFICATION_DIALOG_TEST_ID}
        valid={valid}
        message={message}
        className={null}
        style={markupEditorStyle}
        markup={props.dialogState.certificationNote}
      />
    </>
  );
}

/**
 * Data Quality Service will throw if we send an enabled certification with a blank note
 */
function checkIsValid(current: CertificationDialogState): boolean {
  const command = canSubmitLabel({ message: current.certificationNote, active: current.isCertified });
  return command === 'SUBMIT' || command === 'DELETE';
}

const fetchInitialState = makeFetchInitialStateForLabels<CertificationDialogBodyProps, CertificationDialogState>({
  category: 'certification',
  transform: (t) => ({
    certificationNote: t.message,
    isCertified: t.active,
  })
});

export function getCertificationActionDialogOptions(args: {
  initialBodyProps: CertificationDialogBodyProps;
  initialFormData: CertificationDialogState;
  doAction: (formData: CertificationDialogState) => Promise<unknown>;
}): CertificationActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction } = args;
  const bodyComponent = CertificationDialogBody;

  return {
    title: Messages.certificationLabel(),
    fetchInitialState: FeatureFlags.columnDQWsEnabled ? fetchInitialState : undefined,
    bodyComponent,
    initialBodyProps,
    initialDialogState: initialFormData,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({
      initialFormData,
      currentFormData: dialogState,
      additionalValidation: checkIsValid,
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  };
}
